// Centralised SEO engine — generates titles, descriptions, H1, JSON-LD, canonical URL
// and breadcrumb structures for every page type.
//
// The site URL is taken from a single constant so swapping domains is a one-line change.

export const SITE_URL = 'https://infopointalbania.al';
export const SITE_NAME = 'InfoPoint Albania';
export const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const niceCity = (s) => cap((s || '').replace(/-/g, ' '));

// Master type → readable label / plural / singular slugs
export const TYPE_META = {
  hotel:      { label: 'Hotel',      plural: 'hotels',      singular: 'hotel',      url: '/hotels',      emoji: '🏨' },
  restaurant: { label: 'Restaurant', plural: 'restaurants', singular: 'restaurant', url: '/restaurants', emoji: '🍽️' },
  cafe:       { label: 'Café',       plural: 'cafes',       singular: 'cafe',       url: '/cafes',       emoji: '☕' },
  bar:        { label: 'Bar',        plural: 'bars',        singular: 'bar',        url: '/bars',        emoji: '🍹' },
  beach:      { label: 'Beach',      plural: 'beaches',     singular: 'beach',      url: '/beaches',     emoji: '🏖️' },
  attraction: { label: 'Attraction', plural: 'attractions', singular: 'attraction', url: '/attractions', emoji: '🎭' },
  tour:       { label: 'Tour',       plural: 'tours',       singular: 'tour',       url: '/tours',       emoji: '🗺️' },
  event:      { label: 'Event',      plural: 'events',      singular: 'event',      url: '/events',      emoji: '📅' },
  service:    { label: 'Service',    plural: 'services',    singular: 'service',    url: '/services',    emoji: '🛍️' },
};

export const listingUrl = (type, slug) => {
  const meta = TYPE_META[type] || TYPE_META.attraction;
  return `/${meta.singular}/${slug}`;
};

export const categoryUrl = (type, city) => {
  const meta = TYPE_META[type] || TYPE_META.attraction;
  return city ? `${meta.url}/${city}` : meta.url;
};

export const visitCityUrl = (city) => `/visit-${city}`;

// ---------- TEMPLATES ----------

export const seoHome = ({ country = { name: 'Albania', code: 'albania' } } = {}) => ({
  title: `${SITE_NAME} — Discover Albania's Hidden Gems`,
  description: `The premium guide to ${country.name}: hotels, restaurants, beaches, attractions and hidden gems. AI itineraries, verified listings, real reviews.`,
  canonical: '/',
  keywords: `Albania tourism, visit Albania, Albania hotels, Albania restaurants, Albania beaches, Albania travel guide, Balkans tourism, InfoPoint Albania`,
  h1: `Discover ${country.name}'s Hidden Gems`,
  ogType: 'website',
});

export const seoCategoryHub = (type) => {
  const meta = TYPE_META[type] || TYPE_META.attraction;
  return {
    title: `Best ${meta.plural} in Albania, Kosovo & North Macedonia`,
    description: `Browse the best ${meta.plural} across the Balkans. Hand-picked, verified listings with reviews, photos, prices and direct booking on ${SITE_NAME}.`,
    canonical: meta.url,
    keywords: `best ${meta.plural} Albania, ${meta.plural} Tirana, ${meta.plural} Ksamil, ${meta.plural} Saranda, ${meta.plural} Kosovo, ${meta.plural} North Macedonia, top ${meta.plural} Balkans`,
    h1: `Best ${meta.plural} in the Balkans`,
    ogType: 'website',
  };
};

export const seoCategoryCity = (type, city) => {
  const meta = TYPE_META[type] || TYPE_META.attraction;
  const cityName = niceCity(city);
  return {
    title: `Best ${meta.plural} in ${cityName}, Albania`,
    description: `Top-rated ${meta.plural} in ${cityName} — handpicked by locals. Photos, prices, opening hours and direct WhatsApp booking on ${SITE_NAME}.`,
    canonical: `${meta.url}/${city}`,
    keywords: `${meta.plural} ${cityName}, best ${meta.plural} ${cityName}, ${cityName} ${meta.plural}, top ${meta.plural} ${cityName} Albania, ${cityName} ${meta.label.toLowerCase()} reviews`,
    h1: `Best ${meta.plural} in ${cityName}`,
    ogType: 'website',
  };
};

export const seoListingDetail = (listing) => {
  const meta = TYPE_META[listing.type] || TYPE_META.attraction;
  const cityName = niceCity(listing.city);
  const seoTitle = listing.seo?.title || `${listing.title || listing.name} — ${meta.label} in ${cityName}, Albania`;
  const seoDesc = listing.seo?.description || listing.short_description || (listing.description || '').slice(0, 155);
  return {
    title: seoTitle,
    description: seoDesc,
    canonical: listingUrl(listing.type, listing.slug),
    image: listing.image || (listing.gallery && listing.gallery[0]) || DEFAULT_OG,
    keywords: listing.seo?.keywords || `${listing.title || listing.name}, ${meta.label} ${cityName}, ${cityName} ${meta.label}, best ${meta.label.toLowerCase()} ${cityName}, ${meta.plural} ${cityName} Albania`,
    h1: listing.title || listing.name,
    ogType: meta.singular === 'hotel' ? 'hotel' : 'place',
  };
};

export const seoVisitCity = (city) => {
  const cityName = niceCity(city);
  return {
    title: `Visit ${cityName}, Albania — Complete Travel Guide ${new Date().getFullYear()}`,
    description: `Plan your trip to ${cityName}: best hotels, restaurants, beaches and attractions. Insider tips, daily itineraries, transport guide and booking — all in one place.`,
    canonical: visitCityUrl(city),
    keywords: `visit ${cityName}, ${cityName} travel guide, things to do ${cityName}, ${cityName} Albania, ${cityName} tourism, ${cityName} itinerary, ${cityName} highlights`,
    h1: `Visit ${cityName}`,
    ogType: 'website',
  };
};

// ---------- JSON-LD BUILDERS ----------

export const breadcrumbJsonLd = (trail) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: trail.map((t, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: t.name,
    item: t.url?.startsWith('http') ? t.url : `${SITE_URL}${t.url || '/'}`,
  })),
});

const SCHEMA_TYPE_MAP = {
  hotel: 'Hotel',
  restaurant: 'Restaurant',
  cafe: 'CafeOrCoffeeShop',
  bar: 'BarOrPub',
  beach: 'TouristAttraction',
  attraction: 'TouristAttraction',
  tour: 'TouristTrip',
  event: 'Event',
  service: 'LocalBusiness',
};

export const listingJsonLd = (listing) => {
  const t = SCHEMA_TYPE_MAP[listing.type] || 'LocalBusiness';
  const url = `${SITE_URL}${listingUrl(listing.type, listing.slug)}`;
  const base = {
    '@context': 'https://schema.org',
    '@type': t,
    name: listing.title || listing.name,
    image: listing.gallery && listing.gallery.length ? listing.gallery.slice(0, 6) : [listing.image].filter(Boolean),
    description: listing.short_description || listing.description,
    url,
    address: {
      '@type': 'PostalAddress',
      streetAddress: listing.address,
      addressLocality: niceCity(listing.city),
      addressCountry: cap(listing.country || 'Albania').replace('North-macedonia', 'North Macedonia'),
    },
    telephone: listing.phone,
    priceRange: listing.price,
    ...(listing.hours && { openingHours: listing.hours }),
  };
  if (listing.location?.lat && listing.location?.lng) {
    base.geo = {
      '@type': 'GeoCoordinates',
      latitude: listing.location.lat,
      longitude: listing.location.lng,
    };
  }
  if (listing.rating && listing.reviews_count) {
    base.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(listing.rating),
      reviewCount: String(listing.reviews_count),
      bestRating: '5',
      worstRating: '1',
    };
  }
  if (listing.type === 'hotel' && listing.specialized?.amenities?.length) {
    base.amenityFeature = listing.specialized.amenities.map((a) => ({
      '@type': 'LocationFeatureSpecification',
      name: a,
    }));
  }
  if (listing.type === 'restaurant' && listing.specialized?.cuisine?.length) {
    base.servesCuisine = listing.specialized.cuisine;
  }
  return base;
};

export const itemListJsonLd = (listings, baseUrl) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: (listings || []).slice(0, 24).map((l, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    url: `${SITE_URL}${listingUrl(l.type, l.slug)}`,
    name: l.title || l.name,
  })),
  url: `${SITE_URL}${baseUrl}`,
});

export const placeJsonLd = (city, listings) => ({
  '@context': 'https://schema.org',
  '@type': 'TouristDestination',
  name: niceCity(city.slug || city.name),
  description: city.tagline || `Visit ${niceCity(city.slug || city.name)}, Albania.`,
  image: city.image,
  url: `${SITE_URL}${visitCityUrl(city.slug)}`,
  containedInPlace: { '@type': 'Country', name: 'Albania' },
  touristType: ['Beach', 'Cultural', 'Adventure', 'Culinary'],
  ...(listings?.length && { containsPlace: listings.slice(0, 8).map((l) => ({ '@type': 'LocalBusiness', name: l.title || l.name })) }),
});
